import React from 'react';
import { get } from 'lodash';
import { UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { ActionButton } from 'routes/styles';
import {
  ConditionSetType,
  ConditionType,
  ContactFormType,
  SegmentType,
  UserType,
} from '../../../../../shared/types';
import Icon from '../../../../../shared/components/Icon';
import SwitchButton from '../../../../../shared/components/SwitchButton';
import { ErrorField } from '../../../../../shared/styles';
import {
  ActionButtonContainer,
  ConditionButton,
  SegmentConditionBox,
  TypeAction,
} from './styles';
import { matchTypes } from './constants';
import SegmentConditionRow from './SegmentConditionRow';

type SegmentConditionSetProps = {
  form: UseFormReturn<SegmentType>;
  actionsDisabled?: boolean;
  disabled?: boolean;
  key?: string;
  errors?: any;
  contactForm: ContactFormType;
  conditionSet?: ConditionSetType;
  onDelete: (id: string) => void;
  onChange: (conditionSet: ConditionSetType) => void;
  userInfo?: UserType;
};

const SegmentConditionSet = (props: SegmentConditionSetProps) => {
  const { t } = useTranslation();
  const {
    conditionSet,
    onChange,
    onDelete,
    form,
    contactForm,
    key,
    actionsDisabled,
    disabled,
    errors,
    userInfo,
  } = props;
  const { match_type, conditions } = conditionSet || {};
  const {
    formState: { submitCount },
  } = form;
  const onDeleteCondition = (id: string) => {
    if (disabled) return;
    const newValue = (conditions || []).filter(
      (condition: ConditionType) => condition.id !== id,
    );
    onChange({
      match_type: 'ANY',
      ...conditionSet,
      conditions: newValue,
    });
  };

  const onAddCondition = () => {
    if (disabled) return;
    const newValue = [...(conditions || [])];
    newValue.push({
      id: uuid(),
      name: '',
      evaluate_on: 'CONTACT',
      operator: '',
      value: '',
      field_type: '',
    });
    onChange({
      match_type: 'ANY',
      ...conditionSet,
      conditions: newValue,
    });
  };

  const onChangeCondition = (newCondition: ConditionType) => {
    const newValue: ConditionType[] = (conditions || []).map((condition) =>
      condition.id === newCondition.id ? newCondition : condition,
    );
    onChange({
      match_type: 'ANY',
      ...conditionSet,
      conditions: newValue,
    });
  };

  return (
    <React.Fragment key={key}>
      <SegmentConditionBox
        isErrored={
          submitCount > 0 && Boolean(get(errors, ['conditions', 'message'], ''))
        }
      >
        <TypeAction>
          <Trans
            i18nKey="segments.new_segment.include_contact_condition"
            components={{
              switchButton: (
                <SwitchButton
                  value={match_type}
                  onChange={(item) =>
                    onChange({
                      conditions: [],
                      ...conditionSet,
                      match_type: item.id,
                    })
                  }
                  disabled={disabled}
                  items={matchTypes}
                  itemLabelIdentifier="name"
                  itemValueIdentifier="id"
                />
              ),
            }}
          />
        </TypeAction>
        {conditions?.map((condition: ConditionType, index: number) => {
          return (
            <React.Fragment key={condition.id || `${index}`}>
              <SegmentConditionRow
                key={condition.id || `${index}`}
                errors={get(errors, ['conditions', index], {})}
                onDelete={onDeleteCondition}
                showError={submitCount > 0}
                disabled={disabled}
                condition={condition}
                contactForm={contactForm}
                onChange={onChangeCondition}
                userInfo={userInfo}
              />
              {index < conditions.length - 1 && (
                <ConditionButton>
                  {match_type === 'ALL' ? `${t('and')}` : `${t('or')}`}
                </ConditionButton>
              )}
            </React.Fragment>
          );
        })}
        <ActionButtonContainer>
          <ActionButton
            onClick={onAddCondition}
            className={disabled ? 'disabled' : ''}
          >
            <div>
              <Icon width="12" height="12" glyph="plus" />
            </div>
            <div>{t('workflow.condition.new_condition')}</div>
          </ActionButton>
          <ActionButton
            className={actionsDisabled ? 'disabled' : ''}
            onClick={() => {
              if (actionsDisabled) return;
              onDelete(conditionSet?.id || '');
            }}
          >
            <div>
              <Icon width="12" height="12" glyph="minus" />
            </div>
            <div>{t('segments.new_segment.delete_group')}</div>
          </ActionButton>
        </ActionButtonContainer>
      </SegmentConditionBox>

      {submitCount > 0 && get(errors, ['conditions', 'message'], false) && (
        <ErrorField>{get(errors, ['conditions', 'message'], '')}</ErrorField>
      )}
    </React.Fragment>
  );
};

export default SegmentConditionSet;
